import {
  WeatherForecast,
  WeatherAlert,
  GeoCoordinate,
} from '../types';
import {
  OpenWeatherResponse,
  OpenWeatherForecastResponse,
  NOAAAlertResponse,
} from '../types/api';
import { env } from '../config/env';
import { logger } from '../utils/logger';
import { OpenMeteoService } from './openMeteoService';
import { NOAABuoyService, type NearbyBuoyObservations } from './noaaBuoyService';

// =============================================================================
// WEATHER API CONFIGURATION
// =============================================================================

// NOAA/NWS API endpoints (free, no API key required)
const NOAA_BASE_URL = 'https://api.weather.gov';

// OpenWeatherMap API (backup - requires API key)
const OPENWEATHER_BASE_URL = 'https://api.openweathermap.org/data/2.5';
const OPENWEATHER_API_KEY = env.api.openWeatherMapApiKey;

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

const convertMphToKnots = (mph: number): number => mph / 1.15078;

// =============================================================================
// ENHANCED WEATHER API SERVICE
// =============================================================================

export class WeatherApiService {
  /**
   * Get current weather conditions
   */
  static async getCurrentWeather(location: GeoCoordinate): Promise<WeatherForecast> {
    try {
      logger.info('Fetching current weather from Open-Meteo', { location });
      const [modeled, buoyObservation] = await Promise.all([
        OpenMeteoService.getCurrentWeather(location),
        NOAABuoyService.fetchNearestRecentObservations(location.latitude, location.longitude)
          .catch(error => {
            logger.warn('Nearby NOAA buoy observation unavailable; retaining modeled conditions', {
              location,
              error: error instanceof Error ? error.message : String(error),
            });
            return null;
          }),
      ]);
      return this.mergeBuoyObservations(modeled, buoyObservation);
    } catch (error) {
      logger.error('Error fetching current weather from Open-Meteo', error as Error, { location });
      // Fallback to OpenWeatherMap if available
      if (OPENWEATHER_API_KEY !== undefined && OPENWEATHER_API_KEY !== null && OPENWEATHER_API_KEY !== '') {
        return this.getCurrentWeatherFromOpenWeather(location);
      }
      throw error;
    }
  }

  private static mergeBuoyObservations(
    modeled: WeatherForecast,
    observations: NearbyBuoyObservations | null,
  ): WeatherForecast {
    if (!observations) return modeled;
    const waveBuoy = observations.wave?.buoy;
    const windBuoy = observations.wind?.buoy;
    const observedPeriod = waveBuoy?.dominant_wave_period ?? waveBuoy?.average_wave_period;

    const waves = waveBuoy && Number.isFinite(waveBuoy.wave_height) ? {
      ...modeled.waves,
      height: Math.round((waveBuoy.wave_height as number) * 10) / 10,
      period: Number.isFinite(observedPeriod) ? Math.round(observedPeriod as number) : modeled.waves.period,
      direction: Number.isFinite(waveBuoy.wave_direction) ? waveBuoy.wave_direction as number : modeled.waves.direction,
      // Standard NDBC observations report total significant wave height, not
      // separate wind-sea and swell components. Never imply a decomposition.
      windWaveHeight: undefined,
      windWavePeriod: undefined,
      swellHeight: undefined,
      swellPeriod: undefined,
      swellDirection: undefined,
      dataStatus: 'observed' as const,
      source: Number.isFinite(observedPeriod) ? `NOAA NDBC ${waveBuoy.station_id}` : `NOAA NDBC ${waveBuoy.station_id} height; modeled period`,
      observedAt: waveBuoy.observation_time,
      stationId: waveBuoy.station_id,
      stationDistanceKm: Math.round((observations.wave?.distanceKm ?? 0) * 10) / 10,
      periodType: Number.isFinite(waveBuoy.dominant_wave_period) ? 'dominant' as const :
        Number.isFinite(waveBuoy.average_wave_period) ? 'mean' as const : modeled.waves.periodType,
    } : modeled.waves;

    const wind = windBuoy && Number.isFinite(windBuoy.wind_speed) ? {
      ...modeled.wind,
      speed: Math.round(windBuoy.wind_speed as number),
      direction: Number.isFinite(windBuoy.wind_direction) ? windBuoy.wind_direction as number : modeled.wind.direction,
      gusts: Number.isFinite(windBuoy.wind_gust) ? Math.round(windBuoy.wind_gust as number) : undefined,
      dataStatus: 'observed' as const,
      source: `NOAA NDBC ${windBuoy.station_id}`,
      observedAt: windBuoy.observation_time,
      stationId: windBuoy.station_id,
      stationDistanceKm: Math.round((observations.wind?.distanceKm ?? 0) * 10) / 10,
    } : modeled.wind;

    const atmosphericBuoy = [windBuoy, waveBuoy].find(candidate =>
      Number.isFinite(candidate?.sea_level_pressure) || Number.isFinite(candidate?.visibility)
    );

    return {
      ...modeled,
      wind,
      waves,
      weather: {
        ...modeled.weather,
        pressure: Number.isFinite(atmosphericBuoy?.sea_level_pressure)
          ? (atmosphericBuoy?.sea_level_pressure as number) * 0.02953
          : modeled.weather.pressure,
        visibility: Number.isFinite(atmosphericBuoy?.visibility) ? atmosphericBuoy?.visibility as number : modeled.weather.visibility,
      },
    };
  }

  /**
   * Get 7-day weather forecast with enhanced marine data
   */
  static async getWeatherForecast(
    location: GeoCoordinate,
    days: number = 7
  ): Promise<WeatherForecast[]> {
    try {
      logger.info('Fetching weather forecast from Open-Meteo', { days, location });
      return await OpenMeteoService.getWeatherForecast(location, days);
    } catch (error) {
      logger.error('Error fetching weather forecast from Open-Meteo', error as Error, { location });
      // Fallback to OpenWeatherMap if available
      if (OPENWEATHER_API_KEY !== undefined && OPENWEATHER_API_KEY !== null && OPENWEATHER_API_KEY !== '') {
        return this.getForecastFromOpenWeather(location, days);
      }
      throw error;
    }
  }

  /** Detailed hourly weather and sea-state forecast for voyage planning. */
  static async getHourlyWeatherForecast(
    location: GeoCoordinate,
    days: number = 7
  ): Promise<WeatherForecast[]> {
    return OpenMeteoService.getHourlyWeatherForecast(location, days);
  }

  /**
   * Get weather alerts for the region
   */
  static async getWeatherAlerts(location: GeoCoordinate): Promise<WeatherAlert[]> {
    try {
      // NOAA alerts only work for US territories
      // Check if location is likely in US waters/territory
      const isLikelyUSWaters = (
        location.latitude >= 24 && location.latitude <= 50 &&  // Continental US range
        location.longitude >= -125 && location.longitude <= -66  // Continental US range
      ) || (
        location.latitude >= 17 && location.latitude <= 23 &&  // Caribbean/Puerto Rico
        location.longitude >= -68 && location.longitude <= -64
      ) || (
        location.latitude >= 18 && location.latitude <= 29 &&  // Hawaii region
        location.longitude >= -180 && location.longitude <= -154
      );

      if (!isLikelyUSWaters) {
        logger.info('Location outside NOAA coverage area, skipping weather alerts', { location });
        return [];
      }

      // Create abort controller for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

      try {
        // Get alerts for the specific point
        const alertsResponse = await fetch(
          `${NOAA_BASE_URL}/alerts/active?point=${location.latitude},${location.longitude}`,
          {
            method: 'GET',
            headers: {
              'Accept': 'application/geo+json',
              'User-Agent': 'OceanInsightPro/1.0',
            },
            signal: controller.signal,
          }
        );

        clearTimeout(timeoutId);

        if (!alertsResponse.ok) {
          // Handle specific status codes
          if (alertsResponse.status === 404) {
            logger.info('No NOAA coverage for location (404)', { location });
            return [];
          }
          logger.error('NOAA alerts API error', new Error(`Status: ${alertsResponse.status}`), {
            status: alertsResponse.status,
            statusText: alertsResponse.statusText,
            location,
          });
          return [];
        }

        const alertsData = await alertsResponse.json() as NOAAAlertResponse;

        // Verify we have valid features array
        if (!Array.isArray(alertsData.features)) {
          logger.warn('Invalid NOAA alerts response format', { location });
          return [];
        }

        return this.formatWeatherAlerts(alertsData.features);
      } catch (fetchError) {
        // Clear timeout on error
        clearTimeout(timeoutId);
        throw fetchError; // Re-throw to be caught by outer try-catch
      }
    } catch (error) {
      // Handle different error types
      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          logger.error('NOAA alerts request timed out', error, { location });
        } else if (error.message.includes('Network request failed')) {
          logger.error('Network error fetching NOAA alerts', error, { location });
        } else {
          logger.error('Error fetching weather alerts', error, { location });
        }
      }
      // Always return empty array on error to prevent app crashes
      return [];
    }
  }

  /**
   * Format weather alerts
   */
  private static formatWeatherAlerts(alertFeatures: NOAAAlertResponse['features']): WeatherAlert[] {
    return alertFeatures.map((feature) => {
      const properties = feature.properties;

      return {
        id: properties.id,
        type: this.mapAlertType(properties.event),
        severity: this.mapAlertSeverity(properties.severity),
        title: properties.headline,
        description: properties.description,
        startTime: properties.onset || properties.sent,
        endTime: properties.expires,
        affectedAreas: [], // Would need to parse geometry for exact coordinates
      };
    });
  }

  private static mapAlertType(event: string): 'warning' | 'watch' | 'advisory' {
    if (event.toLowerCase().includes('warning')) return 'warning';
    if (event.toLowerCase().includes('watch')) return 'watch';
    return 'advisory';
  }

  private static mapAlertSeverity(severity: string): 'low' | 'moderate' | 'high' | 'severe' {
    switch (severity?.toLowerCase()) {
      case 'minor':
        return 'low';
      case 'moderate':
        return 'moderate';
      case 'severe':
        return 'high';
      case 'extreme':
        return 'severe';
      default:
        return 'moderate';
    }
  }


  /**
   * Fallback to OpenWeatherMap API
   */
  private static async getCurrentWeatherFromOpenWeather(
    location: GeoCoordinate
  ): Promise<WeatherForecast> {
    if (OPENWEATHER_API_KEY === undefined || OPENWEATHER_API_KEY === null || OPENWEATHER_API_KEY === '') {
      throw new Error('OpenWeatherMap API key not configured');
    }

    const response = await fetch(
      `${OPENWEATHER_BASE_URL}/weather?lat=${location.latitude}&lon=${location.longitude}&appid=${OPENWEATHER_API_KEY}&units=imperial`
    );

    if (!response.ok) {
      throw new Error('Failed to get weather from OpenWeatherMap');
    }

    const data = (await response.json()) as OpenWeatherResponse;

    // Format OpenWeatherMap data to our format
    return {
      id: `owm-current-${location.latitude}-${location.longitude}`,
      timestamp: new Date().toISOString(),
      location,
      wind: {
        speed: convertMphToKnots(data.wind.speed),
        direction: data.wind.deg,
        gusts: data.wind.gust !== undefined ? convertMphToKnots(data.wind.gust) : undefined,
      },
      waves: {
        height: 0,
        period: 0,
        direction: 0,
        dataStatus: 'unavailable',
        source: 'Marine model unavailable',
      },
      weather: {
        temperature: data.main.temp,
        humidity: data.main.humidity,
        pressure: data.main.pressure * 0.02953, // Convert hPa to inHg
        visibility: data.visibility !== undefined ? data.visibility * 0.000539957 : 10, // Convert m to nm
        precipitation: 0,
        conditions: data.weather[0].description,
      },
      uvIndex: 0, // Not supplied by this fallback API.
      sunrise: new Date(data.sys.sunrise * 1000).toISOString(),
      sunset: new Date(data.sys.sunset * 1000).toISOString(),
    };
  }


  private static async getForecastFromOpenWeather(
    location: GeoCoordinate,
    days: number
  ): Promise<WeatherForecast[]> {
    if (OPENWEATHER_API_KEY === undefined || OPENWEATHER_API_KEY === null || OPENWEATHER_API_KEY === '') {
      throw new Error('OpenWeatherMap API key not configured');
    }

    const response = await fetch(
      `${OPENWEATHER_BASE_URL}/forecast?lat=${location.latitude}&lon=${location.longitude}&appid=${OPENWEATHER_API_KEY}&units=imperial`
    );

    if (!response.ok) {
      throw new Error('Failed to get forecast from OpenWeatherMap');
    }

    const data = (await response.json()) as OpenWeatherForecastResponse;

    // Format OpenWeatherMap forecast data
    const forecasts: WeatherForecast[] = [];
    const dailyForecasts = data.list.filter((_, index) => index % 8 === 0).slice(0, days); // Every 8th item (daily)

    dailyForecasts.forEach((forecast, index) => {
      forecasts.push({
        id: `owm-forecast-${location.latitude}-${location.longitude}-${index}`,
        timestamp: new Date(forecast.dt * 1000).toISOString(),
        location,
        wind: {
          speed: convertMphToKnots(forecast.wind.speed),
          direction: forecast.wind.deg,
          gusts: forecast.wind.gust !== undefined ? convertMphToKnots(forecast.wind.gust) : undefined,
        },
        waves: {
          height: 0,
          period: 0,
          direction: 0,
          dataStatus: 'unavailable',
          source: 'Marine model unavailable',
        },
        weather: {
          temperature: forecast.main.temp,
          humidity: forecast.main.humidity,
          pressure: forecast.main.pressure * 0.02953,
          visibility: 10,
          precipitation: forecast.rain?.['3h'] ?? 0,
          conditions: forecast.weather[0].description,
        },
        uvIndex: 0, // Not supplied by this fallback API.
        sunrise: new Date().toISOString(),
        sunset: new Date().toISOString(),
      });
    });

    return forecasts;
  }
}
